/**
 * What the connection is doing, said out loud.
 *
 * §8.3: "App Platform deploys drop WebSocket connections; Yjs reconnects and
 * resyncs cleanly." The reconnect is genuinely clean — but only for the
 * document. For the person, a deploy is a minute of typing into a box that looks
 * exactly like a box that is saving, and the difference between "keep going,
 * this is buffered" and "stop, nothing you type is leaving this tab" is a
 * sentence they can only get from here.
 *
 * `connectionState` is a pure function of the snapshot so the wording is
 * inspectable and testable rather than tangled into JSX.
 */

import type { ReactElement } from 'react';
import { Badge, type BadgeTone } from '../components/ui/badge.tsx';
import { cn } from '../components/ui/cn.ts';
import type { CollabSnapshot } from './collab-session.ts';

export interface ConnectionState {
  label: string;
  tone: BadgeTone;
  /** The sentence that changes what the person does next; null when nothing is wrong. */
  detail: string | null;
  /** Whether edits made now are still only in this tab. */
  offline: boolean;
}

export function connectionState(snapshot: CollabSnapshot): ConnectionState {
  if (snapshot.status === 'denied') {
    // `api/collab.ts`: only `unauthenticated` is worth branching on, because it
    // is the only one whose remedy is an action rather than an explanation. The
    // rest collapse to "you may not edit this" on purpose — telling a missing
    // node from a refused one is how a socket enumerates the tree.
    return snapshot.denial === 'unauthenticated'
      ? {
          label: 'Signed out',
          tone: 'error',
          detail: 'Your session ended. Reload the page and sign in to keep editing.',
          offline: true,
        }
      : {
          label: 'Read-only',
          tone: 'error',
          detail: 'You do not have edit access to this document, so nothing here is being saved.',
          offline: true,
        };
  }

  if (snapshot.status === 'disconnected') {
    return {
      label: 'Reconnecting',
      tone: 'warning',
      detail:
        'The connection dropped — a deploy will do this. Your edits are held in this tab and ' +
        'will sync when it comes back. Do not close the tab.',
      offline: true,
    };
  }

  if (snapshot.status === 'idle' || snapshot.status === 'connecting') {
    return { label: 'Connecting', tone: 'gray', detail: null, offline: true };
  }

  // Connected, but the first handshake has not finished: the document on screen
  // is not yet the document on the server.
  if (!snapshot.synced) {
    return { label: 'Syncing', tone: 'gray', detail: null, offline: true };
  }

  if (snapshot.unsynced > 0) {
    return { label: 'Saving', tone: 'gray', detail: null, offline: false };
  }

  return { label: 'Saved', tone: 'success', detail: null, offline: false };
}

/**
 * The indicator itself. Rendered only for an editable surface — a reader is not
 * connected to anything, and a badge saying so would be answering a question
 * they never asked.
 */
export function ConnectionIndicator({
  snapshot,
  className,
}: {
  snapshot: CollabSnapshot;
  className?: string;
}): ReactElement {
  const state = connectionState(snapshot);
  return (
    <span className={cn('inline-flex items-center gap-2', className)}>
      <Badge tone={state.tone}>
        <span
          aria-hidden="true"
          className={cn(
            'size-1.5 rounded-full bg-current',
            state.offline && state.tone !== 'gray' && 'animate-pulse',
          )}
        />
        {state.label}
      </Badge>
      {state.detail ? <span className="text-xs text-tertiary">{state.detail}</span> : null}
    </span>
  );
}
